import type { OrderClaim } from './OrderClaims';

export const CLAIM_STAGES = [
  ['cancel_requested', '취소 접수'], ['cancel_progress', '취소 진행'], ['cancel_completed', '취소 완료'],
  ['return_requested', '반품 접수'], ['return_progress', '반품 진행'], ['return_completed', '반품 완료'],
  ['refund_requested', '환불 접수'], ['refund_progress', '환불 진행'], ['refund_completed', '환불 완료'],
] as const;

export function claimStage(claim: Pick<OrderClaim, 'kind' | 'status' | 'refund_status'>) {
  if (claim.kind === 'return' && claim.status === 'received') return 'return_completed';
  if (claim.status === 'unavailable' || claim.refund_status === 'unavailable') return `${claim.kind}_unavailable`;
  if (claim.refund_status === 'completed') return `${claim.kind}_completed`;
  return `${claim.kind}_${claim.status === 'requested' && !['processing', 'review'].includes(claim.refund_status) ? 'requested' : 'progress'}`;
}

export function claimStageLabel(claim: Pick<OrderClaim, 'kind' | 'status' | 'refund_status'>) {
  return CLAIM_STAGES.find(([key]) => key === claimStage(claim))?.[1]
    || `${({ cancel: '취소', return: '반품', refund: '환불' })[claim.kind]} 불가`;
}

export const CLAIM_PROCESSING_STATES = [['requested','접수'],['approved','승인'],['collecting','회수 중'],['received','회수 완료'],['completed','처리 완료'],['unavailable','처리 불가']] as const;
export const CLAIM_REFUND_STATES = [['none','환불 전 / 없음'],['pending','환불 대기'],['processing','환불 중'],['review','환불 확인 필요'],['completed','환불 완료'],['unavailable','환불 불가']] as const;
export function claimProcessingState(claim: Pick<OrderClaim,'kind'|'status'|'refund_status'>) {
  if (claim.status==='unavailable') return 'unavailable';
  if (claim.kind!=='return' && claim.refund_status==='completed') return 'completed';
  return claim.status;
}
export function claimProcessingLabel(claim: Pick<OrderClaim,'kind'|'status'|'refund_status'>) {
  const state=claimProcessingState(claim), kind={cancel:'취소',return:'반품',refund:'환불'}[claim.kind];
  if(state==='collecting') return '회수 중';
  if(state==='received') return '회수 완료';
  return `${kind} ${({requested:'접수',approved:'승인',completed:'처리 완료',unavailable:'불가'} as Record<string,string>)[state]||'상태 확인 필요'}`;
}
export function claimRefundLabel(claim: {kind?:string;refund_status:string;transfer_status?:string|null}) {
  if(claim.refund_status==='none') return claim.kind?'환불 전':'환불 없음';
  if(claim.transfer_status==='pending') return '송금 대기';
  if(claim.transfer_status==='completed') return '송금 완료';
  return CLAIM_REFUND_STATES.find(([value])=>value===claim.refund_status)?.[1]||'환불 상태 확인 필요';
}

export function clampClaimQuantity(value: string, maximum: number, minimum = 0) {
  if (value === '') return '';
  const number = Number(value), limit = Math.max(0, Math.trunc(maximum) || 0), floor = Math.min(limit, Math.max(0, minimum));
  return String(Math.min(limit, Math.max(floor, Number.isFinite(number) ? Math.trunc(number) : floor)));
}

export function claimTime(value?: string | null) {
  if (!value) return '-';
  // API claim dates are UTC; older detail dates already carry the Korean display format.
  const date = new Date(/Z$|[+-]\d\d:\d\d$/.test(value) ? value : `${value.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
  return parts;
}
